import { ChangeDetectorRef, Component, OnInit, ChangeDetectionStrategy, input, output, signal, OnDestroy } from '@angular/core';
import { firstValueFrom, interval, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { RingJoinRequestsComponent } from 'src/app/restricted-requests/ring-join-requests/ring-join-requests.component';
import { GameStoreDispatcher } from 'src/app/core/state/game/game.dispatcher';
import { GameToLoad } from 'src/app/core/state/game/game.state';
import { NavController } from '@ionic/angular';
import { NavigationExtras } from '@angular/router';
import { SopoLoadingService } from 'src/app/shared/services/sopo-loading/sopo-loading.service';
import { ClubRole, GameServerStatus, GameType, IBombPotConfig, IMultiTableTournamentState, ITableState, ITournamentState, LimitType, TableType, TournamentStatus } from '@soposopo/sopo-models';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { ClubService } from 'src/app/core/firebase/club.service';
import { IStateClubGame } from 'src/app/core/state/clubLobby/clubLobby.state';
import { collection, doc, docData, DocumentReference, Firestore, getCountFromServer, Timestamp } from '@angular/fire/firestore';
import { SopoAlertSettings } from 'src/app/sopo-popup/interfaces/alert';
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { RingConfig } from 'src/app/core/state/freePlayLobby/freePlayLobby.state';
import { SopoTransitionService } from '../../services/sopo-transition/sopo-transition.service';

@Component({
    selector: 'app-game-tables',
    templateUrl: './game-tables.component.html',
    styleUrls: ['./game-tables.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GameTablesComponent implements OnInit, OnDestroy{
    game = input.required<IStateClubGame | RingConfig>();
    isClub = input.required<boolean>(); //false in freeplay
    isUnion = input<boolean>(false); //false in freeplay
    mainStageGame = input<boolean>(false); //false in freeplay
    tableId = input<string | null>(null);
    gameSelected = output<RingConfig>();
    
    private ngUnsubscribe = new Subject<void>();
    
    clubSubscription: Subscription; //no need in freeplay
    balSubscription: Subscription; //no need in freeplay
    isAdmin = false; //false in freeplay
    alertPresent = false; //false in freeplay
    memberBalance = 0; //no need in freeplay
    ringTable = signal<RingTable>({...INIT_RING_TABLE}); 
    freeplayRingTable = signal<FreeplayRingTable>(INIT_FREEPLAY_RING_TABLE);
    sngTable = signal<SngTable>({...INIT_SNG_TABLE});
    mttTable = signal<MttTable>({...INIT_MTT_TABLE});
    role: ClubRole = ClubRole.MEMBER; //no need in freeplay
    myId: string;
    gameInitialized = false;
    registrationRequestCountSubscription: Subscription; //no need in freeplay

    tableSubscription: Subscription;
    
    sopoDateOptions: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    };

    constructor(
        private gameDispatcher: GameStoreDispatcher,
        public navCtrl: NavController,
        public loadingService: SopoLoadingService,
        private sopoTransitionService: SopoTransitionService,
        private firestore: Firestore,
        private ref: ChangeDetectorRef,
        private profileDispatcher: ProfileStateDispatcher,
        private clubService: ClubService,
        private sopoPopupService: SopoPopupService,
    ) { }

    async ngOnInit() {
        this.myId = await this.profileDispatcher.getMyID();

        // Club
        if(this.isClub()) {
            this.initializeClubSubscriptions();
            if(this.game().type === TableType.RING) {
                const docReference = doc(this.firestore, `tables/${(this.game() as IStateClubGame).id}`)
                this.tableSubscription = docData(docReference).pipe(
                        takeUntil(this.ngUnsubscribe),
                        filter(table => table != undefined),
                ).subscribe((table: ITableState) => {
                    if (table !== undefined) {
                        this.ringTable.update((oldVal) => {
                            return {...oldVal,
                                type: TableType.RING,
                                tableName: table.name,
                                tableID: table.id,
                                createdDate: (table.dateCreated as unknown as Timestamp).seconds,
                                gameType: table.rules.gameMode.gameType,
                                limitType: table.rules.gameMode.limitType,
                                seats: table.seats.length,
                                restricted: table.settings.restricted,
                                restrictedRequests: table.joinToTableRequests.length,
                                amIPlaying: table.seats.indexOf(this.myId) > -1,
                                minBuyIn: table.rules.minBuyIn,
                                maxBuyIn: table.rules.maxBuyIn,
                                gpsRestriction: table.settings.gpsRestriction,
                                isAcceptedAtTable: table.acceptedJoinToTableRequests.indexOf(this.myId) > -1 ? true : false,
                                isRequestedAlready: table.joinToTableRequests.indexOf(this.myId) > -1 ? true : false,
                                gameServer: table.server,
                                waitinglist: table.waitingList,
                                deleteTable: table.deleteTable,
                                notEmptySeats: table.seats.reduce((acc, val) => val !== null ? ++acc : acc, 0),
                            }
                        });
                        this.gameInitialized = true;
                    }
                });
            }
            else if(this.game().type === TableType.SNG){
                const docReference = doc(this.firestore, `tournaments/${(this.game() as IStateClubGame).id}`)
                const tournamentSub = docData(docReference).pipe(
                    takeUntil(this.ngUnsubscribe),
                ).subscribe((tournament: ITournamentState) => {
                    if (tournament !== undefined) {
                        this.sngTable.update((oldVal) => {
                            return {...oldVal,
                                type: TableType.SNG,
                                tableName: tournament.settings.name,
                                tableID: tournament.id,
                                createdDate: (tournament.dateCreated as unknown as Timestamp).seconds,
                                gameType: tournament.rules.gameMode.gameType,
                                limitType: tournament.rules.gameMode.limitType,
                                seats: tournament.rules.seats,
                                restricted: tournament.settings.restricted,
                                restrictedRequests: tournament.joinToTournamentsRequests ? tournament.joinToTournamentsRequests.length : 0,
                                amIPlaying: tournament.playerIDs.indexOf(this.myId) > -1,
                                gpsRestriction: tournament.settings.restricted,
                                buyIn: tournament.rules.buyIn,
                                isDeleted: tournament.isDeleted,
                            }
                        });
                        this.gameInitialized = true;
                    }
                });
            }
            else{
                const docReference = doc(this.firestore, `tournaments/${(this.game() as IStateClubGame).id}`)
                const tournamentSub = docData(docReference).pipe(
                    takeUntil(this.ngUnsubscribe),
                ).subscribe((tournament: IMultiTableTournamentState) => {
                        if (tournament !== undefined) {
                            this.mttTable.update((oldVal) => {
                                return {...oldVal,
                                    type: TableType.MTT,
                                    tableName: tournament.settings.name,
                                    tableID: tournament.id,
                                    createdDate: (tournament.dateCreated as unknown as Timestamp).seconds,
                                    gameType: tournament.rules.gameMode.gameType,
                                    limitType: tournament.rules.gameMode.limitType,
                                    seats: tournament.rules.maxPlayersPerTable,
                                    restricted: tournament.settings.restricted,
                                    requireKyc: tournament.settings.requireKyc,
                                    amIPlaying: tournament.playerIDs.indexOf(this.myId) > -1,
                                    blindsTime: tournament.rules.blindsTime,
                                    startingDate: tournament.settings.startingDate/1000,
                                    buyIn: tournament.rules.buyIn,
                                    status: tournament.status as TournamentStatus,
                                    isBeforeStart: [TournamentStatus.WAITING_FOR_PLAYERS, TournamentStatus.READY_TO_START_TOURNAMENT, TournamentStatus.GAME_START_IN_WAITING].find(status => tournament.status === status) !== undefined,
                                    isDeleted: tournament.isDeleted,
                                }
                            })
                            if (tournament.settings.restricted && this.isAdmin)
                                this.handleRegistraionRequestSubscription((this.game() as IStateClubGame).id, tournament.status as TournamentStatus);
                            this.gameInitialized = true;
                        }
                        this.ref.markForCheck();
                    });
                this.ref.markForCheck();
            }
        }
        // Freeplay
        else {
            if (this.tableId()) {
                const docReference = doc(this.firestore, `tables/${this.tableId()}`) as DocumentReference<ITableState>;
                this.tableSubscription = docData(docReference).pipe(
                        takeUntil(this.ngUnsubscribe),
                        filter(table => table != undefined),
                ).subscribe((table: ITableState) => {
                    if (table !== undefined) {
                        this.freeplayRingTable.update((oldVal) =>{
                            return {...oldVal,
                                type: TableType.RING,
                                tableName: table.name,
                                tableID: table.id,
                                createdDate: (table.dateCreated as unknown as Timestamp).seconds,
                                gameType: table.rules.gameMode.gameType,
                                limitType: table.rules.gameMode.limitType,
                                seats: table.seats.length,
                                amIPlaying: table.seats.indexOf(this.myId) > -1,
                                minBuyIn: table.rules.minBuyIn,
                                maxBuyIn: table.rules.maxBuyIn,
                                gpsRestriction: table.settings.gpsRestriction,
                                gameServer: table.server,
                                deleteTable: table.deleteTable,
                                notEmptySeats: table.seats.reduce((acc, val) => val !== null ? ++acc : acc, 0),
                                bombPotConfig: table.rules.bombPotConfig ?? null,
                            };
                        })
                        this.gameInitialized = true;
                    }
                });
            } else {
                this.freeplayRingTable.set({
                    type: TableType.RING,
                    tableName: (this.game() as RingConfig).tableName,
                    tableID: null,
                    createdDate: Date.now() / 1000,
                    gameType: (this.game() as RingConfig).gameMode.gameType,
                    limitType: (this.game() as RingConfig).gameMode.limitType,
                    seats: (this.game() as RingConfig).seats,
                    amIPlaying: false,
                    minBuyIn: (this.game() as RingConfig).minBuyIn,
                    maxBuyIn: (this.game() as RingConfig).maxBuyIn,
                    gpsRestriction: (this.game() as RingConfig).gpsRestriction,
                    gameServer: {state: 'UnAllocated', gameServerName: ''},
                    deleteTable: false,
                    notEmptySeats: 0,
                    restricted: false,
                    restrictedRequests: 0,
                    isUnion: false,
                    bombPotConfig: (this.game() as RingConfig).bombPotConfig ?? null,
                });
            }
        }
    }

    private async initializeClubSubscriptions() {
        if (this.clubSubscription) this.clubSubscription.unsubscribe();
        const role$ = this.clubService.getRoleObservable(this.myId);
        this.clubSubscription = role$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(role => {
                this.role = role;
                this.isAdmin = this.role === ClubRole.OWNER || this.role === ClubRole.MANAGER;
                if (this.isAdmin && this.game().type === TableType.MTT && this.gameInitialized && this.mttTable().restricted)
                    this.handleRegistraionRequestSubscription((this.game() as IStateClubGame).id, this.mttTable().status);
                this.ref.markForCheck();
            })
        if (this.balSubscription) this.balSubscription.unsubscribe();
        this.balSubscription = this.clubService.getMemberBalanceObservable(this.myId)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((balance) => {
                this.memberBalance = Math.round(balance * 100) / 100;
                this.ref.markForCheck();
            });
    }

    handleRegistraionRequestSubscription(tournamentId: string, status: TournamentStatus) {
        if (
            status === TournamentStatus.WAITING_FOR_PLAYERS ||
            status === TournamentStatus.LATE_REGISTRATION_OPEN_AND_MTT_IN_PROGRESS ||
            status === TournamentStatus.GAME_START_IN_WAITING
        ) {
            if (this.registrationRequestCountSubscription && !this.registrationRequestCountSubscription.closed) return;
            this.registrationRequestCountSubscription = interval(5000).subscribe(async () => {
                const coll = collection(this.firestore, `tournaments/${tournamentId}/joinRequests`);
                const count = (await getCountFromServer(coll)).data().count;
                if (
                    this.registrationRequestCountSubscription &&
                    !this.registrationRequestCountSubscription.closed &&
                    count !== this.mttTable().restrictedRequests
                ) {
                    this.mttTable.update(val => ({ ...val, restrictedRequests: count }));
                }
            });
        } else if (this.registrationRequestCountSubscription && !this.registrationRequestCountSubscription.closed) {
            this.registrationRequestCountSubscription.unsubscribe();
            this.mttTable.update(val => ({ ...val, restrictedRequests: 0 }));
        }
    }

    async tableRedirect(tableId: string) { // RING tables
        if (this.isClub() && !this.gameInitialized) return;
        const gamesList = await firstValueFrom(this.gameDispatcher.getState().pipe(
            map(gameState => gameState.gamesList))
        );
        const isTableAlreadyOpened = gamesList.findIndex(game => game.componentType === 'game' && game.gameStateId === tableId) > -1;
        if (gamesList.length >= 4 && !isTableAlreadyOpened) { 
            this.sopoPopupService.clearViewContainer('app');
            const sopoAlertSettings: SopoAlertSettings = {
                header: `You can't open more than 4 tables.`,
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            };
            this.sopoPopupService.showAlert('app', sopoAlertSettings);
            return;
        }
        if (!this.isClub()) { // freeplay navigate to table
            this.gameSelected.emit(this.game() as RingConfig);
            return;
        }
        try {
            this.loadingService.present('general');
            if(this.tableId()) tableId = this.tableId();
            const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '', clubId: this.getClubId(), type: TableType.RING };
            this.gameDispatcher.addNewGame(game, true);
        } catch (err) {
            if(!this.isClub()) await this.sopoTransitionService.closeTransition();
            console.error(err);
        } finally {
            this.loadingService.dismiss('general');
        }
    }

    waitingListShowCheck(table: RingTable) { // no waiting list in freeplay
        const playersInPlayAndWaitingList = table.notEmptySeats + table.waitinglist.length;
        if (
            playersInPlayAndWaitingList >= table.seats
            && (!table.restricted || (table.restricted && (table.isAcceptedAtTable || this.isAdmin)))
            && !table.amIPlaying
            && table.minBuyIn
            && this.memberBalance >= (table.minBuyIn)
        ) return true;
        return false;
    }

    public async navigateToTable(tournamentId: string, tournamentType: string) { // Tournament tables
        if (!this.gameInitialized) return;
        this.loadingService.present('general');
        sessionStorage.setItem('tournamentType', tournamentType);
        sessionStorage.setItem('tournamentId', tournamentId);
        const navigationExtras: NavigationExtras = {
            state: {
                type: tournamentType,
            },
            replaceUrl: true,
        };
        this.loadingService.dismiss('general');
        this.navCtrl.navigateForward(`tournament-registration/${this.getClubId()}/${tournamentId}`, navigationExtras);
    }

    getClubId() {
        return (this.game() as IStateClubGame).clubId ?? "SOPO"
    }

    getGameModeLimitType(limitType: string): string {
        return limitType.split(' ').map(word => word[0]).join('').toUpperCase();
    }

    manageJoinRequests(tableID: string, restrictedRequests: number, event: MouseEvent): void {
        if (!this.isClub()) return;
        if (restrictedRequests > 0 && this.isAdmin && this.game().type === TableType.RING) {
            event.stopImmediatePropagation();
            this.sopoPopupService.clearViewContainer('app');
            this.sopoPopupService.openDialog('app', RingJoinRequestsComponent, {
                closeButton: true,
                data: {
                    tableId: tableID,
                    isTableInfo: false,
                },
            });
        }
    }

    ngOnDestroy(): void {
        this.ref.detach();
        this.destroySubscriptions();
    }

    private destroySubscriptions() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.tableSubscription?.unsubscribe();

        if (this.clubSubscription) this.clubSubscription.unsubscribe();
        if (this.balSubscription) this.balSubscription.unsubscribe();
        if (this.registrationRequestCountSubscription) this.registrationRequestCountSubscription.unsubscribe();
    }
}

type Seconds = number;
export interface CommonTableProps {
    type: TableType;
    tableName: string;
    tableID: string;
    createdDate: number;
    gameType: GameType;
    limitType: LimitType;
    seats: number;
    restricted: boolean;
    restrictedRequests: number;
    amIPlaying: boolean;
    isUnion: boolean;
}

export const INIT_COMMON_TABLE_PROPS: CommonTableProps = {
    type: TableType.RING,
    tableName: '',
    tableID: '',
    createdDate: Date.now(),
    gameType: GameType.HoldEm,
    limitType: LimitType.NoLimit,
    seats: 0,
    restricted: false,
    restrictedRequests: 0,
    amIPlaying: false,
    isUnion: false,
}

export interface RingTable extends CommonTableProps {
    waitinglist: Array<string>;
    blinds: string;
    minBuyIn: number;
    maxBuyIn: number;
    gpsRestriction: boolean;
    isAcceptedAtTable: boolean;
    isRequestedAlready: boolean;
    gameServer: GameServerStatus;
    deleteTable: boolean;
    notEmptySeats: number;
}

export const INIT_RING_TABLE: RingTable = {
    ...INIT_COMMON_TABLE_PROPS,
    waitinglist: [],
    blinds: '',
    minBuyIn: 0,
    maxBuyIn: 0,
    gpsRestriction: false,
    isAcceptedAtTable: false,
    isRequestedAlready: false,
    gameServer: {state: 'UnAllocated', gameServerName: ''},
    deleteTable: false,
    notEmptySeats: 0,
}

export interface SngTable extends CommonTableProps {
    gpsRestriction: boolean;
    buyIn: number;
    isDeleted: boolean;
}

export const INIT_SNG_TABLE: SngTable = {
    ...INIT_COMMON_TABLE_PROPS,
    gpsRestriction: false,
    buyIn: 0,
    isDeleted: false,
}

export interface MttTable extends CommonTableProps {
    blindsTime: number;
    startingDate: Seconds;
    buyIn: number;
    status: TournamentStatus;
    isBeforeStart: boolean;
    isDeleted: boolean;
    requireKyc: boolean;
}

export const INIT_MTT_TABLE: MttTable = {
    ...INIT_COMMON_TABLE_PROPS,
    blindsTime: 0,
    startingDate: Date.now(),
    buyIn: 0,
    status: TournamentStatus.WAITING_FOR_PLAYERS,
    isBeforeStart: true,
    isDeleted: false,
    requireKyc: false,
}

export interface FreeplayRingTable extends CommonTableProps  {
    minBuyIn: number;
    maxBuyIn: number;
    gpsRestriction: boolean;
    gameServer: GameServerStatus;
    deleteTable: boolean;
    notEmptySeats: number;
    bombPotConfig: IBombPotConfig | null;
}

export const INIT_FREEPLAY_RING_TABLE: FreeplayRingTable = {
    type: TableType.RING,
    tableName: '',
    tableID: '',
    createdDate: Date.now(),
    gameType: GameType.HoldEm,
    limitType: LimitType.NoLimit,
    seats: 0,
    amIPlaying: false,
    minBuyIn: 0,
    maxBuyIn: 0,
    gpsRestriction: false,
    gameServer: { state: 'UnAllocated', gameServerName: '' },
    deleteTable: false,
    notEmptySeats: 0,
    restricted: false,
    restrictedRequests: 0,
    isUnion: false,
    bombPotConfig: null,
}
